import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus } from 'lucide-react';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { useCreateClient } from '@/api/clients';
import { useUserDirectory } from '@/api/users';
import { useActiveDiagnoses } from '@/api/referenceData';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField, Input } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { PageHeader } from '@/components/layout/PageHeader';
import { SelectField } from './SelectField';

/*
 * Minimal create form — the required server fields plus status and an
 * optional BCBA assignment. The full demographics / insurance / guardian
 * fields are collected afterwards on the detail page's edit view, so
 * intake staff can get a record on the books in seconds and finish
 * later.
 */

const NEW_CLIENT_STATUSES = ['Active', 'Pending', 'Waitlist'] as const;

/** Local-time YYYY-MM-DD for `today` — matches what the <input type="date">
 *  picker treats as "today" so the max attribute and the Zod refine agree. */
function todayLocalIso(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

const schema = z.object({
    first:  z.string().trim().min(1, 'First name is required'),
    last:   z.string().trim().min(1, 'Last name is required'),
    dob:    z.string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date of birth is required')
        .refine((v) => v <= todayLocalIso(), 'Date of birth cannot be in the future'),
    dx1Id:  z.string().trim().min(1, 'Primary diagnosis is required'),
    status: z.enum(NEW_CLIENT_STATUSES),
    bcbaId: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

export function ClientNewPage() {
    const navigate = useNavigate();
    const createMut = useCreateClient();
    const bcbas = useUserDirectory('BCBA');
    const diagnoses = useActiveDiagnoses();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { status: 'Pending' },
    });

    async function onSubmit(values: FormValues) {
        try {
            const created = await createMut.mutateAsync({
                first:  values.first.trim(),
                last:   values.last.trim(),
                dob:    values.dob,
                dx1Id:  values.dx1Id,
                status: values.status,
                bcbaId: values.bcbaId || null,
            });
            navigate(`/clients/${created.id}`, { replace: true });
        } catch {
            /* server error is surfaced below via createMut.error */
        }
    }

    const serverError = createMut.error
        ? extractErrorMessage(createMut.error, 'Failed to create client')
        : null;

    return (
        <div className="p-4 sm:p-6 max-w-2xl mx-auto">
            <PageHeader
                back={{ to: '/clients', label: 'Back to clients' }}
                title="Add client"
                subtitle="Required info first — demographics, insurance, and guardian details can be filled in after the record is created."
            />

            <Card>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    {serverError && (
                        <Alert variant="danger" className="mb-4">
                            {serverError}
                        </Alert>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        <FormField label="First name" required error={errors.first?.message}>
                            {(id) => <Input id={id} autoComplete="off" {...register('first')} />}
                        </FormField>
                        <FormField label="Last name" required error={errors.last?.message}>
                            {(id) => <Input id={id} autoComplete="off" {...register('last')} />}
                        </FormField>
                        <FormField label="Date of birth" required error={errors.dob?.message}>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="date"
                                    min="1900-01-01"
                                    max={todayLocalIso()}
                                    {...register('dob')}
                                />
                            )}
                        </FormField>
                        <FormField label="Status" required error={errors.status?.message}>
                            {(id) => (
                                <SelectField id={id} {...register('status')}>
                                    {NEW_CLIENT_STATUSES.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                        <FormField
                            label="Primary diagnosis"
                            required
                            error={errors.dx1Id?.message}
                            className="sm:col-span-2"
                        >
                            {(id) => (
                                <SelectField id={id} {...register('dx1Id')} disabled={diagnoses.isLoading || diagnoses.isError}>
                                    <option value="">
                                        {diagnoses.isLoading
                                            ? 'Loading…'
                                            : diagnoses.isError
                                                ? 'Diagnoses unavailable'
                                                : 'Select a diagnosis'}
                                    </option>
                                    {(diagnoses.data ?? []).map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.code ? `${d.code} — ${d.name}` : d.name}
                                        </option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                        <FormField label="Assigned BCBA" error={errors.bcbaId?.message} className="sm:col-span-2">
                            {(id) => (
                                <SelectField id={id} {...register('bcbaId')}>
                                    <option value="">Unassigned (set later)</option>
                                    {(bcbas.data ?? []).map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.first} {u.last}{u.roleName !== 'BCBA' ? ` — ${u.roleName}` : ''}
                                        </option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-border">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => navigate('/clients')}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={isSubmitting}>
                            <Plus className="w-4 h-4" />
                            {isSubmitting ? 'Creating…' : 'Create client'}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
}
